import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { CalendarDays, Clock3, FileText } from "lucide-react";

import { api } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { SelfPunchCards } from "../components/SelfPunchCard";
import { Card, PageHeader } from "../components/ui";
import { localDateInTimezone } from "../lib/attendance";
import { formatDate, formatDateTime } from "../lib/format";
import { formatMinutes } from "../lib/people";

export function StaffDeskPage() {
  const { user } = useAuth();
  const timezone = user?.account?.timezone;

  const today = useMemo(() => localDateInTimezone(timezone), [timezone]);

  const shifts = useQuery({
    queryKey: ["my-shifts", today],
    queryFn: ({ signal }) => api.shiftsPage({ from: today, to: today, page_size: 10 }, signal),
  });

  const todayShift = (shifts.data?.results as any)?.[0];

  return (
    <>
      <PageHeader
        title="My Desk"
        subtitle={'Hello — your shift, check-in, leave and latest payslip.'}
      />

      <SelfPunchCards />

      <section className="mt-8 grid gap-5 lg:grid-cols-[2fr_1fr]">
        <Card>
          <header className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <h2 className="text-base font-semibold text-foreground">Shift overview</h2>
              <p className="text-sm text-muted-foreground">
                {todayShift ? formatDate((todayShift as any).work_date || (todayShift as any).shift_date || today) : "No shift assigned for today."}
              </p>
            </div>
            <Link className="text-sm font-medium text-primary hover:underline" to="/people/attendance">
              View attendance
            </Link>
          </header>
          {todayShift ? (
            <dl className="mt-4 grid gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-border/60 p-3">
                <dt className="flex items-center gap-2 text-xs text-muted-foreground">
                  <Clock3 className="h-4 w-4" />
                  Start / End
                </dt>
                <dd className="mt-1 text-sm font-medium text-foreground">
                  {formatDateTime((todayShift as any).start_at || (todayShift as any).start_time)} – {formatDateTime((todayShift as any).end_at || (todayShift as any).end_time)}
                </dd>
              </div>
              <div className="rounded-lg border border-border/60 p-3">
                <dt className="flex items-center gap-2 text-xs text-muted-foreground">
                  <CalendarDays className="h-4 w-4" />
                  Scheduled
                </dt>
                <dd className="mt-1 text-sm font-medium text-foreground">
                  {formatMinutes((todayShift as any).scheduled_minutes || 0)}
                </dd>
              </div>
              <div className="rounded-lg border border-border/60 p-3">
                <dt className="flex items-center gap-2 text-xs text-muted-foreground">
                  <FileText className="h-4 w-4" />
                  Location
                </dt>
                <dd className="mt-1 text-sm font-medium text-foreground">
                  {(todayShift as any).location?.name ?? "—"}
                </dd>
              </div>
            </dl>
          ) : null}
        </Card>
      </section>
    </>
  );
}
